import { useMemo } from 'react';
import { Box, Menu, MenuItem, Stack, styled, Theme, Typography } from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import { Flag, OutlinedButton } from '@shared/components';
import { usePopupElement } from '@shared/hooks';
import { brandLogo } from '@retail/utils';
import { Image } from '../Image';
import { LanguageCapitalLettersCode } from '@shared/types';

export interface BrandSelectOption {
  name: string;
  key: string;
  countryCode: 'SE' | 'NO';
  logoSrc?: string;
}

const LogoPlate = styled(Box)(({ theme }) => ({
  display: 'flex',
  ...theme.applyStyles('dark', {
    backgroundColor: theme.vars.palette.common.white,
    borderRadius: '50%',
    padding: 3,
  }),
}));

interface Props {
  brands: BrandSelectOption[];
  selectedBrandKey: string;
  onBrandSelect: (brandKey: string) => void;
}

export function BrandSelect({ brands, selectedBrandKey, onBrandSelect }: Props) {
  const selectedBrand = useMemo(
    () => brands.find((b) => b.key === selectedBrandKey) as BrandSelectOption,
    [brands, selectedBrandKey]
  );
  const hasMultipleCountryAccess = useMemo(
    () => new Set(brands.map(({ countryCode }) => countryCode)).size > 1,
    [brands]
  );

  const { menuElementProps, triggerElementProps } = usePopupElement();
  const enableMenu = useMemo(() => brands.length > 1, [brands]);

  return (
    <>
      <OutlinedButton
        sx={(theme: Theme) => ({
          cursor: enableMenu ? 'pointer' : 'default',
          minWidth: 200,
          height: 43,
          justifyContent: 'space-between',

          '&:hover,  &.Mui-selected': {
            background: theme.vars.palette.background.paper,
            border: `2px solid ${theme.vars.palette.primary.main}`,
          },
          background: theme.vars.palette.grey[50],
          border: `2px solid ${theme.vars.palette.grey[50]}`,
          ...theme.applyStyles('dark', {
            '&:hover,  &.Mui-selected': {
              border: `2px solid ${theme.vars.palette.primary.light}`,
            },
          }),
        })}
        size="small"
        endIcon={
          enableMenu && (
            <ChevronLeft
              sx={{
                transform: menuElementProps.open ? 'rotate(90deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease-in-out',
                color: 'text.primary',
              }}
            />
          )
        }
        {...triggerElementProps}
      >
        <Stack direction="row" alignItems="center" whiteSpace="nowrap" gap={2}>
          <LogoPlate>
            <Image
              src={brandLogo(selectedBrand.key).logo}
              alt={selectedBrand.name}
              height={30}
              width={30}
            />
          </LogoPlate>
          <Typography variant="body1" color="text.primary">
            {selectedBrand.name}
          </Typography>
          {hasMultipleCountryAccess && (
            <Flag country={selectedBrand.countryCode as LanguageCapitalLettersCode} size="20px" />
          )}
        </Stack>
      </OutlinedButton>

      {enableMenu && (
        <Menu
          {...menuElementProps}
          elevation={0}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              sx: {
                boxShadow: '4px 4px 10px -1px',
              },
            },
          }}
        >
          {brands.map((brand) => (
            <MenuItem
              key={brand.key}
              value={brand.key}
              onClick={() => {
                onBrandSelect(brand.key);
              }}
              selected={brand.key === selectedBrand.key}
              sx={{ justifyContent: 'space-between' }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <LogoPlate>
                  <Image
                    src={brandLogo(brand.key).circularLogo}
                    alt={brand.name}
                    height={25}
                    width={25}
                  />
                </LogoPlate>
                <Typography>{brand.name}</Typography>
              </Stack>
              {hasMultipleCountryAccess && (
                <Flag country={brand.countryCode as LanguageCapitalLettersCode} size="20px" />
              )}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}
